import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Star, Heart, Minus, Plus, Truck, RefreshCw, Shield, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useApp } from '../context/AppContext';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ProductPage() {
  const { productId } = useParams();
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist } = useApp();

  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  // Review form
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: '',
    reviewer_name: '',
    reviewer_email: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  const inWishlist = product ? isInWishlist(product.id) : false;
  usePageTitle(product?.name || 'Shop');

  const optionTypes = product?.option_types || [];
  const variants = product?.variants || [];
  const hasOptions = optionTypes.length > 0;

  // Resolve the currently selected variant
  const allOptionsSelected = optionTypes.every((ot) => selectedOptions[ot.name]);
  const selectedVariant = !product
    ? null
    : !hasOptions
      ? variants[0] || null
      : allOptionsSelected
        ? variants.find((v) => optionTypes.every((ot) => v.options?.[ot.name] === selectedOptions[ot.name])) || null
        : null;

  const variantOnSale = selectedVariant?.sale_price && selectedVariant.sale_price < selectedVariant.price;
  const currentStock = selectedVariant ? selectedVariant.stock : (product?.total_stock ?? 0);

  // Resolve an image tied to the currently selected option value (last match wins = most specific)
  const optionValueImages = product?.option_value_images || {};
  let variantImage = null;
  optionTypes.forEach((ot) => {
    const val = selectedOptions[ot.name];
    const img = val && optionValueImages[ot.name]?.[val];
    if (img) variantImage = img;
  });

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const [productRes, reviewsRes] = await Promise.all([
          axios.get(`${API_URL}/products/${productId}`),
          axios.get(`${API_URL}/products/${productId}/reviews`)
        ]);
        setProduct(productRes.data);
        setReviews(reviewsRes.data);

        // Default to the first in-stock variant's exact option combo (guarantees a valid selection)
        const vs = productRes.data.variants || [];
        const firstChoice = vs.find((v) => (v.stock || 0) > 0) || vs[0];
        if (firstChoice && firstChoice.options && Object.keys(firstChoice.options).length > 0) {
          setSelectedOptions({ ...firstChoice.options });
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  // Reset quantity if it exceeds available stock when the variant changes
  useEffect(() => {
    if (selectedVariant && quantity > selectedVariant.stock) {
      setQuantity(selectedVariant.stock > 0 ? 1 : 1);
    }
  }, [selectedVariant]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show the variant's image first when the selection changes
  useEffect(() => {
    setSelectedImage(0);
  }, [variantImage]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectOption = (typeName, value) => {
    setSelectedOptions((prev) => ({ ...prev, [typeName]: value }));
  };

  const handleAddToCart = async () => {
    if (!product) return;

    if (hasOptions && !allOptionsSelected) {
      toast.error('Please select all options');
      return;
    }
    if (!selectedVariant) {
      toast.error('This combination is unavailable');
      return;
    }
    if (selectedVariant.stock < 1) {
      toast.error('Selected option is out of stock');
      return;
    }

    try {
      await addToCart(product.id, quantity, selectedVariant.id);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    }
  };

  const handleWishlistToggle = () => {
    if (inWishlist) {
      removeFromWishlist(product.id);
      toast.success('Removed from wishlist');
    } else {
      addToWishlist(product.id);
      toast.success('Added to wishlist');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);

    try {
      const response = await axios.post(`${API_URL}/products/${productId}/reviews`, {
        ...reviewForm,
        product_id: productId
      });

      setReviews([response.data, ...reviews]);
      setReviewForm({
        rating: 5,
        title: '',
        comment: '',
        reviewer_name: '',
        reviewer_email: ''
      });

      const productRes = await axios.get(`${API_URL}/products/${productId}`);
      setProduct(productRes.data);

      toast.success('Review submitted!');
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-square bg-muted rounded-xl animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-muted rounded w-3/4" />
            <div className="h-6 bg-muted rounded w-1/4" />
            <div className="h-24 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Product not found</h1>
        <Link to="/shop">
          <Button>Back to Shop</Button>
        </Link>
      </div>
    );
  }

  const displayPrice = selectedVariant
    ? (variantOnSale ? selectedVariant.sale_price : selectedVariant.price)
    : product.price_from;

  const galleryImages = variantImage
    ? [variantImage, ...(product.images || []).filter((i) => i !== variantImage)]
    : (product.images || []);

  return (
    <div className="page-transition">
      {/* Breadcrumb */}
      <div className="bg-muted/30 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/shop">Shop</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to={`/shop?category=${encodeURIComponent(product.category)}`}>{product.category}</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square rounded-2xl overflow-hidden bg-muted">
              <img
                src={galleryImages?.[selectedImage] || 'https://images.unsplash.com/photo-1559709451-1b5dc3c0038c?w=800'}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </div>
            {galleryImages?.length > 1 && (
              <div className="flex gap-3">
                {galleryImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === index ? 'border-[#EA580C]' : 'border-transparent'
                    }`}
                  >
                    <img src={image} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">
                {product.category}
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="product-name">
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center gap-2 mb-4">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`h-5 w-5 ${
                        star <= product.avg_rating
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-slate-200'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground">
                  ({product.review_count} reviews)
                </span>
              </div>

              {/* Price */}
              <div className="flex items-center gap-3 mb-6">
                {!selectedVariant && hasOptions ? (
                  <span className="text-3xl font-bold text-foreground" data-testid="product-price">
                    From {formatPrice(product.price_from)}
                  </span>
                ) : variantOnSale ? (
                  <>
                    <span className="text-3xl font-bold text-[#EA580C]" data-testid="product-price">
                      {formatPrice(selectedVariant.sale_price)}
                    </span>
                    <span className="text-xl text-muted-foreground line-through">
                      {formatPrice(selectedVariant.price)}
                    </span>
                    <span className="bg-[#EA580C] text-white text-sm px-3 py-1 rounded-full">
                      Save {Math.round((1 - selectedVariant.sale_price / selectedVariant.price) * 100)}%
                    </span>
                  </>
                ) : (
                  <span className="text-3xl font-bold text-foreground" data-testid="product-price">
                    {formatPrice(displayPrice)}
                  </span>
                )}
              </div>

              <p className="text-muted-foreground leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Option Type Selectors */}
            {optionTypes.map((ot) => (
              <div key={ot.name} data-testid={`option-type-${ot.name}`}>
                <Label className="text-sm font-medium mb-3 block">
                  {ot.name}:{' '}
                  <span className="font-normal text-muted-foreground">
                    {selectedOptions[ot.name] || 'Select'}
                  </span>
                </Label>
                <div className="flex flex-wrap gap-3">
                  {ot.values.map((value) => {
                    const active = selectedOptions[ot.name] === value;
                    return (
                      <button
                        key={value}
                        onClick={() => selectOption(ot.name, value)}
                        className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                          active
                            ? 'border-[#EA580C] bg-[#EA580C]/5 text-[#EA580C]'
                            : 'border-border hover:border-foreground'
                        }`}
                        data-testid={`option-${ot.name}-${value}`}
                      >
                        {value}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Quantity */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Quantity</Label>
              <div className="quantity-selector inline-flex">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  data-testid="quantity-decrease"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <input
                  type="text"
                  value={quantity}
                  readOnly
                  className="font-medium"
                  data-testid="quantity-value"
                />
                <button
                  onClick={() => setQuantity(Math.min(currentStock || 1, quantity + 1))}
                  disabled={quantity >= currentStock}
                  data-testid="quantity-increase"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              {selectedVariant && currentStock <= 5 && currentStock > 0 && (
                <p className="text-sm text-amber-600 mt-2">Only {currentStock} left in stock</p>
              )}
            </div>

            {/* Unavailable combination notice */}
            {hasOptions && allOptionsSelected && !selectedVariant && (
              <p className="text-sm text-red-600 -mt-2" data-testid="combo-unavailable">
                This combination is not available. Please choose different options.
              </p>
            )}

            {/* Actions */}
            <div className="flex gap-4 pt-4">
              <Button
                onClick={handleAddToCart}
                disabled={hasOptions ? (!selectedVariant || currentStock === 0) : currentStock === 0}
                className="flex-1 bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full py-6 text-lg font-medium btn-primary"
                data-testid="add-to-cart-btn"
              >
                {hasOptions && allOptionsSelected && !selectedVariant
                  ? 'Unavailable'
                  : (selectedVariant && currentStock === 0)
                    ? 'Out of Stock'
                    : 'Add to Cart'}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleWishlistToggle}
                className={`h-14 w-14 rounded-full ${inWishlist ? 'text-[#EA580C] border-[#EA580C]' : ''}`}
                data-testid="wishlist-toggle-btn"
              >
                <Heart className={`h-5 w-5 ${inWishlist ? 'fill-current' : ''}`} />
              </Button>
            </div>

            {/* SKU */}
            {(selectedVariant?.sku || product.sku) && (
              <p className="text-sm text-muted-foreground mono">
                SKU: {selectedVariant?.sku || product.sku}
              </p>
            )}

            {/* Trust Badges */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-border">
              <div className="flex items-center gap-3">
                <Truck className="h-5 w-5 text-[#EA580C]" />
                <span className="text-sm">Free NZ shipping over $100</span>
              </div>
              <div className="flex items-center gap-3">
                <RefreshCw className="h-5 w-5 text-[#EA580C]" />
                <span className="text-sm">30-day returns</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-[#EA580C]" />
                <span className="text-sm">Secure checkout</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs: Reviews */}
        <div className="mt-16">
          <Tabs defaultValue="reviews" className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0">
              <TabsTrigger
                value="reviews"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#EA580C] data-[state=active]:text-[#EA580C] pb-4 px-6"
              >
                Reviews ({reviews.length})
              </TabsTrigger>
              <TabsTrigger
                value="write-review"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#EA580C] data-[state=active]:text-[#EA580C] pb-4 px-6"
              >
                Write a Review
              </TabsTrigger>
            </TabsList>

            <TabsContent value="reviews" className="pt-8">
              {reviews.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-4">No reviews yet. Be the first to review this product!</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="review-card" data-testid={`review-${review.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">{review.reviewer_name}</span>
                            {review.verified_purchase && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                Verified Purchase
                              </span>
                            )}
                          </div>
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`h-4 w-4 ${
                                  star <= review.rating
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-slate-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {new Date(review.created_at).toLocaleDateString('en-NZ', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <h4 className="font-medium mb-2">{review.title}</h4>
                      <p className="text-muted-foreground">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="write-review" className="pt-8">
              <form onSubmit={handleSubmitReview} className="max-w-2xl space-y-6" data-testid="review-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="reviewer_name">Your Name *</Label>
                    <Input
                      id="reviewer_name"
                      value={reviewForm.reviewer_name}
                      onChange={(e) => setReviewForm({ ...reviewForm, reviewer_name: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reviewer_email">Your Email *</Label>
                    <Input
                      id="reviewer_email"
                      type="email"
                      value={reviewForm.reviewer_email}
                      onChange={(e) => setReviewForm({ ...reviewForm, reviewer_email: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Rating *</Label>
                  <div className="flex gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                        className="p-1"
                      >
                        <Star
                          className={`h-8 w-8 transition-colors ${
                            star <= reviewForm.rating
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-slate-200 hover:text-amber-200'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="review_title">Review Title *</Label>
                  <Input
                    id="review_title"
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                    placeholder="Sum up your experience"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="review_comment">Your Review *</Label>
                  <Textarea
                    id="review_comment"
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    placeholder="Tell us about your experience with this product"
                    rows={5}
                    required
                    className="mt-1"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submittingReview}
                  className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full px-8"
                  data-testid="submit-review-btn"
                >
                  {submittingReview ? 'Submitting...' : 'Submit Review'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
